/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import 'regenerator-runtime/runtime';

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Bundle, isNull } from '_core/util';
import {
  ApiCredentialsType,
  EndPointsNames,
  InitApiType,
  InitializeTypes,
  KeyValueStringType,
} from '_core/types';
import { getEndPoints } from './Endpoints';
import { configureMock } from '../mock_api';
import { configureAxiosIntLogger } from './AxiosInterceptor';
import { Logger } from '../logger';
import { ApiException, ConfigException } from '_core/exceptions';

const SMILESTOKEN = 'smilesToken';
class AxiosHelper {
  private static instance: AxiosInstance = axios.create();

  async request(
    method: AxiosRequestConfig['method'],
    apiDomain: EndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    const result = prepareUrlAndHeaderByType(
      apiDomain,
      path,
      init.pathParameters,
    );
    const options: AxiosRequestConfig = {
      method,
      url: result.url,
    };
    if (!isNull(init.body)) {
      options.data = init.body;
    }
    if (!isNull(init.params)) {
      options.params = init.params;
    }
    if (!isNull(init.headers)) {
      options.headers = init.headers;
    }
    if (!isNull(init.proxy)) {
      options.proxy = init.proxy;
    }

    if (result.header) {
      options.headers = { ...options.headers, ...result.header };
    }
    const headerAuthorization = getHeaderAuthorization(credentialsType);
    if (headerAuthorization !== null) {
      options.headers = { ...options.headers, ...headerAuthorization };
    }

    return AxiosHelper.instance.request(options);
  }

  public get(
    apiDomain: EndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('GET', apiDomain, path, credentialsType, init);
  }
  public post(
    apiDomain: EndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('POST', apiDomain, path, credentialsType, init);
  }
  public put(
    apiDomain: EndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('PUT', apiDomain, path, credentialsType, init);
  }
  public delete(
    apiDomain: EndPointsNames,
    path: string,
    credentialsType: ApiCredentialsType,
    init: InitApiType,
  ) {
    return this.request('DELETE', apiDomain, path, credentialsType, init);
  }
  public configure(config: InitializeTypes): string[] {
    let lstMocksEnabled: string[] = [];
    try {
      configureAxios(AxiosHelper.instance, config);
    } catch (error) {
      Logger.error(error.message);
      throw new ConfigException({ message: error.message, resource: 'axios' });
    }
    try {
      lstMocksEnabled = configureMock(AxiosHelper.instance, config);
    } catch (error) {
      Logger.error(error.message);
      throw new ConfigException({ message: error.message, resource: 'mocks' });
    }
    return lstMocksEnabled;
  }
  public setAuthToken(token: string) {
    setAuthToken(AxiosHelper.instance, token);
  }
  public saveTokens(token: string, type: ApiCredentialsType) {
    Bundle.setValue(`TOKEN_${type}`, token);
  }
  public saveSmilesToken(token: string) {
    Bundle.setValue(SMILESTOKEN, token);
  }
  public removeTokens(...type: ApiCredentialsType[]) {
    type.forEach((element) => {
      Bundle.clearValue(`TOKEN_${element}`);
    });
  }
  public removeSmilesToken() {
    Bundle.clearValue(SMILESTOKEN);
  }
}

const setAuthToken = (instance: AxiosInstance, token: string) => {
  if (token) {
    instance.defaults.headers.common['Authorization'] = token;
  } else {
    delete instance.defaults.headers.common['Authorization'];
  }
};

const configureAxios = (instance: AxiosInstance, config: InitializeTypes) => {
  instance.defaults.timeout = config.service.timeout;
  configureAxiosIntLogger(instance, config);
};

const prepareUrlAndHeaderByType = (
  apiName: EndPointsNames,
  path: string,
  pathParameters: KeyValueStringType,
): any => {
  const config = Bundle.getValue('config');
  const lstEndpoints = getEndPoints(config.service.environment);
  const endpointFind = lstEndpoints.filter(
    (endPoint) => endPoint.name === apiName,
  );
  if (isNull(endpointFind) || endpointFind.length == 0) {
    throw new ApiException({
      statusCode: '999',
      errorCode: '999',
      errorMessage: 'Endpoint not found',
      message: '',
    });
  }
  let newPath = path;
  if (pathParameters && pathParameters !== null) {
    newPath = replacePathParameters(newPath, pathParameters);
  }

  const { endpoint, type } = endpointFind[0];

  if (endpoint.charAt(endpoint.length - 1) === '/') {
    if (newPath.charAt(0) === '/') {
      newPath = newPath.substring(1);
    }
  } else {
    if (newPath.charAt(0) !== '/') {
      newPath = `/${newPath}`;
    }
  }
  const url = `${endpoint}${newPath}`;
  let header = null;
  if (type === 'Smiles') {
    let channel;
    if (config.channel && config.channel.toUpperCase() === 'WEB') {
      channel = isMobile.any()
        ? 'Mobile'
        : config.channel.charAt(0).toUpperCase() +
          config.channel.toLowerCase().slice(1);
    } else {
      channel = config.channel;
    }

    header = {
      region: config.service.region,
      channel,
    };
  }
  const result = {
    url,
    header,
  };
  return result;
};

const replacePathParameters = (
  path: string,
  pathParameters: KeyValueStringType,
) => {
  Object.entries(pathParameters || {}).forEach(([k, v]) => {
    path = path.replace(`:${k}`, encodeURIComponent(v));
  });
  return path;
};

const getCredentials = (credentialsType: ApiCredentialsType) => {
  const credentialsJson = Bundle.getValue(`TOKEN_${credentialsType}`);
  if (credentialsJson && credentialsJson !== null)
    return JSON.parse(credentialsJson);

  return null;
};

const getHeaderAuthorization = (credentialsType: ApiCredentialsType) => {
  let headerAuthorization = null;
  switch (credentialsType) {
    case 'TOKEN_CREDENTIALS': {
      const credentials = getCredentials(credentialsType);
      if (credentials) {
        headerAuthorization = {
          Authorization: `Bearer ${credentials.access_token}`,
        };
      }
      break;
    }
    case 'TOKEN_AUTH_USER': {
      const credentials = getCredentials(credentialsType);
      if (credentials) {
        headerAuthorization = {
          Authorization: `Bearer ${credentials.accessToken}`,
        };
      }
      break;
    }
    case 'TOKEN_AUTH_USER_OR_CREDENTIALS': {
      let credentials = getCredentials('TOKEN_AUTH_USER');
      if (credentials && credentials.accessToken) {
        headerAuthorization = {
          Authorization: `Bearer ${credentials.accessToken}`,
        };
      } else {
        credentials = getCredentials('TOKEN_CREDENTIALS');
        if (credentials) {
          headerAuthorization = {
            Authorization: `Bearer ${credentials.access_token}`,
          };
        }
      }
      break;
    }
    case 'TOKEN_X_API_KEY': {
      headerAuthorization = {
        'x-api-key': getCredentials(credentialsType),
      };
      break;
    }
    default:
      break;
  }
  return headerAuthorization;
};

const isMobile = {
  Android: function () {
    return navigator.userAgent.match(/Android/i);
  },
  BlackBerry: function () {
    return navigator.userAgent.match(/BlackBerry/i);
  },
  iOS: function () {
    return navigator.userAgent.match(/iPhone|iPod/i);
  },
  Opera: function () {
    return navigator.userAgent.match(/Opera Mini/i);
  },
  Windows: function () {
    return (
      navigator.userAgent.match(/IEMobile/i) ||
      navigator.userAgent.match(/WPDesktop/i)
    );
  },
  any: function () {
    return (
      isMobile.Android() ||
      isMobile.BlackBerry() ||
      isMobile.iOS() ||
      isMobile.Opera() ||
      isMobile.Windows()
    );
  },
};

const instance = new AxiosHelper();
export default instance;
